import { ResponsiveTable } from '../components/ResponsiveTable';
import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { 
  Plus, 
  Search, 
  Edit, 
  Eye, 
  Trash2, 
  Building2, 
  MapPin, 
  Phone, 
  Mail, 
  Globe,
  Clock,
  Calendar,
  Loader2,
  AlertCircle,
  CheckCircle,
  XCircle,
  Filter
} from 'lucide-react';
import { HelpIcon } from '../components/HelpIcon';
import { TableScrollContainer } from '../components/TableScrollContainer';
import { useVisibleSelection } from '../hooks/useVisibleSelection';
import { ConfirmDefinitiveDeleteModal } from '../components/ConfirmDefinitiveDeleteModal';
import { getApiUrl } from '../utils/api';
import { RootState } from '../store';

interface Company {
  id: string;
  name: string;
  legalName: string;
  siret?: string;
  vatNumber?: string;
  address: {
    street: string;
    city: string;
    postalCode: string;
    country: string;
  };
  phone: string;
  email: string;
  website?: string;
  timezone: string;
  defaultSlotSizeMinutes: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface CompaniesResponse {
  success: boolean;
  data: {
    companies: Company[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  message?: string;
}

export function CompaniesListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const canHardDelete = user?.role === 'super_admin';

  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState<{ isOpen: boolean; companyIds: string[] }>({
    isOpen: false,
    companyIds: [],
  });
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkHardDeleteConfirm, setBulkHardDeleteConfirm] = useState<{ isOpen: boolean; companyIds: string[] }>({
    isOpen: false,
    companyIds: [],
  });
  const [bulkHardDeleting, setBulkHardDeleting] = useState(false);

  const visibleCompanyIds = useMemo(() => companies.map((c) => c.id), [companies]);
  const {
    selectedIds: selectedCompanyIds,
    selectedCount,
    selectedArray: selectedCompanyArray,
    clearSelection,
    selectAllCheckboxRef,
    allVisibleSelected,
    toggleSelect,
    toggleSelectAllVisible,
  } = useVisibleSelection<string>(visibleCompanyIds);

  useEffect(() => {
    loadCompanies();
  }, [currentPage, searchTerm, statusFilter]);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20',
        sortKey: 'createdAt',
        sortDir: 'desc'
      });

      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter) params.append('status', statusFilter);

      const response = await fetch(`/api/admin/companies?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch companies');
      }

      const data: CompaniesResponse = await response.json();
      
      if (data.success) {
        setCompanies(data.data.companies);
        setTotalPages(data.data.totalPages);
        setTotal(data.data.total);
      } else {
        setError(data.message || 'Failed to load companies');
      }
    } catch (error) {
      console.error('Companies fetch error:', error);
      setError('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (companyId: string, companyName: string) => {
    if (!confirm(`${t('common.confirmDelete', { name: companyName })} ${t('common.cannotUndo')}`)) {
      return;
    }

    try {
      setDeletingId(companyId);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`/api/admin/companies/${companyId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete company');
      }

      // Reload companies list
      await loadCompanies();
    } catch (error) {
      console.error('Delete company error:', error);
      alert(t('common.deleteError'));
    } finally {
      setDeletingId(null);
    }
  };

  const openBulkDeleteConfirm = () => {
    if (selectedCompanyIds.size === 0) return;
    setBulkDeleteConfirm({ isOpen: true, companyIds: Array.from(selectedCompanyIds) });
  };
  const cancelBulkDelete = () => setBulkDeleteConfirm({ isOpen: false, companyIds: [] });

  const openBulkHardDeleteConfirm = () => {
    if (!canHardDelete || selectedCompanyIds.size === 0) return;
    setBulkHardDeleteConfirm({ isOpen: true, companyIds: selectedCompanyArray });
  };
  const cancelBulkHardDelete = () => setBulkHardDeleteConfirm({ isOpen: false, companyIds: [] });
  const confirmBulkHardDelete = async () => {
    if (bulkHardDeleteConfirm.companyIds.length === 0) return;
    try {
      setBulkHardDeleting(true);
      setError(null);
      const token = localStorage.getItem('token');
      const failed: string[] = [];
      for (const id of bulkHardDeleteConfirm.companyIds) {
        try {
          const resp = await fetch(getApiUrl(`/api/admin/companies/${id}?hardDelete=true`), {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          });
          if (!resp.ok) failed.push(id);
        } catch {
          failed.push(id);
        }
      }
      setBulkHardDeleteConfirm({ isOpen: false, companyIds: [] });
      clearSelection();
      await loadCompanies();
      if (failed.length > 0) setError(`${failed.length} entreprise(s) n'ont pas pu être supprimées définitivement.`);
    } finally {
      setBulkHardDeleting(false);
    }
  };

  const confirmBulkDelete = async () => {
    if (bulkDeleteConfirm.companyIds.length === 0) return;
    try {
      setBulkDeleting(true);
      setError(null);
      const token = localStorage.getItem('token');

      const failed: Array<{ id: string; message: string }> = [];

      for (const companyId of bulkDeleteConfirm.companyIds) {
        try {
          const resp = await fetch(`/api/admin/companies/${companyId}`, {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          if (!resp.ok) {
            failed.push({ id: companyId, message: 'Failed to delete company' });
          }
        } catch {
          failed.push({ id: companyId, message: 'Une erreur est survenue lors de la suppression.' });
        }
      }

      setBulkDeleteConfirm({ isOpen: false, companyIds: [] });
      clearSelection();
      await loadCompanies();

      if (failed.length > 0) {
        setError(`${failed.length} entreprise(s) n’ont pas pu être supprimées.`);
      }
    } finally {
      setBulkDeleting(false);
    }
  };

  const getStatusBadge = (isActive: boolean) => {
    if (isActive) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="w-3 h-3 mr-1" />
          {t('navigation.companies.active')}
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <XCircle className="w-3 h-3 mr-1" />
          {t('navigation.companies.inactive')}
        </span>
      );
    }
  };

  const formatAddress = (address: Company['address']) => {
    return `${address.street}, ${address.city} ${address.postalCode}, ${address.country}`;
  };

  if (loading && companies.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center space-x-2">
          <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          <span className="text-gray-600">{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="dt-list-toolbar flex justify-between items-center">
        <div className="flex items-center space-x-3">
          <div>
            <h1 className="text-3xl font-display font-bold text-textPrimary flex items-center space-x-2">
              <span>{t('navigation.companies.title')}</span>
              <HelpIcon 
                docPath="03-entreprises#03-2-liste" 
                tooltip="Documentation : Liste des entreprises"
              />
            </h1>
            <p className="text-textSecondary mt-1">
              {total} {t('navigation.companies.totalCount', { count: total })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/companies/create')}
            className="btn-primary inline-flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>{t('navigation.companies.create')}</span>
          </button>
          {selectedCount > 0 && (
            <div className="ml-3 flex items-center space-x-2">
              <button
                onClick={openBulkDeleteConfirm}
                disabled={bulkDeleting || bulkHardDeleting || deletingId !== null}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 inline-flex items-center space-x-2"
                title={t('navigation.companies.delete')}
              >
                <Trash2 className="w-4 h-4" />
                <span>Supprimer ({selectedCount})</span>
              </button>
              {canHardDelete && (
                <button
                  onClick={openBulkHardDeleteConfirm}
                  disabled={bulkDeleting || bulkHardDeleting || deletingId !== null}
                  className="px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50 inline-flex items-center space-x-2"
                  title="Suppression définitive"
                >
                  <AlertCircle className="w-4 h-4" />
                  <span>Suppression définitive ({selectedCount})</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <ConfirmDefinitiveDeleteModal
        isOpen={bulkHardDeleteConfirm.isOpen}
        onConfirm={confirmBulkHardDelete}
        onClose={cancelBulkHardDelete}
        entityLabel={bulkHardDeleteConfirm.companyIds.length !== 1 ? 'sociétés' : 'société'}
        count={bulkHardDeleteConfirm.companyIds.length}
        isLoading={bulkHardDeleting}
      />

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="dt-filters flex flex-nowrap items-center gap-3">
          <div className="relative flex-1 min-w-0 max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
            <input
              type="text"
              placeholder={t('navigation.companies.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-10 w-full"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input-field shrink-0 w-[180px]"
          >
            <option value="">{t('navigation.companies.allStatuses')}</option>
            <option value="active">{t('navigation.companies.active')}</option>
            <option value="inactive">{t('navigation.companies.inactive')}</option>
          </select>
          <button
            onClick={() => { setSearchTerm(''); setStatusFilter(''); setCurrentPage(1); }}
            className="btn-secondary flex items-center justify-center space-x-2 shrink-0"
          >
            <Filter className="w-4 h-4" />
            <span>Effacer</span>
          </button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800 font-medium">{error}</span>
          </div>
          <button
            onClick={loadCompanies}
            className="mt-2 text-sm text-red-600 hover:text-red-700 underline"
          >
            {t('common.retry')}
          </button>
        </div>
      )}

      {/* Companies Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {companies.length === 0 ? (
          <div className="text-center py-12">
            <Building2 className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {t('navigation.companies.noCompanies')}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {t('navigation.companies.noCompaniesDescription')}
            </p>
            <div className="mt-6">
              <button
                onClick={() => navigate('/companies/create')}
                className="btn-primary inline-flex items-center space-x-2"
              >
                <Plus className="w-4 h-4" />
                <span>{t('navigation.companies.create')}</span>
              </button>
            </div>
          </div>
        ) : (
          <TableScrollContainer hint="Faites défiler →">
            <ResponsiveTable className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                    <input
                      ref={selectAllCheckboxRef}
                      type="checkbox"
                      checked={allVisibleSelected}
                      disabled={companies.length === 0 || bulkDeleting || deletingId !== null}
                      onChange={toggleSelectAllVisible}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      aria-label="Sélectionner toutes les entreprises de la page"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('navigation.companies.name')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('navigation.companies.contact')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('navigation.companies.address')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('navigation.companies.settings')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('navigation.companies.status')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 z-10 border-l border-gray-200">
                    {t('navigation.companies.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {companies.map((company) => (
                  <tr key={company.id} className="group hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedCompanyIds.has(company.id)}
                        disabled={bulkDeleting || deletingId !== null}
                        onChange={() => toggleSelect(company.id)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        aria-label={`Sélectionner l'entreprise ${company.name}`}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-lg bg-primary-100 flex items-center justify-center">
                            <Building2 className="h-5 w-5 text-primary-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {company.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {company.legalName}
                          </div>
                          {company.siret && (
                            <div className="text-xs text-gray-400">
                              {t('navigation.companies.siret')}: {company.siret}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <div className="flex items-center">
                          <Phone className="w-3 h-3 mr-1 text-gray-400" />
                          {company.phone}
                        </div>
                        <div className="flex items-center mt-1">
                          <Mail className="w-3 h-3 mr-1 text-gray-400" />
                          {company.email}
                        </div>
                        {company.website && (
                          <div className="flex items-center mt-1">
                            <Globe className="w-3 h-3 mr-1 text-gray-400" />
                            {company.website}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        <div className="flex items-start">
                          <MapPin className="w-3 h-3 mr-1 mt-0.5 text-gray-400 flex-shrink-0" />
                          <span>{formatAddress(company.address)}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <div className="flex items-center">
                          <Clock className="w-3 h-3 mr-1 text-gray-400" />
                          {company.timezone}
                        </div>
                        <div className="flex items-center mt-1">
                          <Calendar className="w-3 h-3 mr-1 text-gray-400" />
                          {company.defaultSlotSizeMinutes} {t('navigation.companies.minSlots')}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(company.isActive)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium sticky right-0 bg-white group-hover:bg-gray-50 z-10 border-l border-gray-200">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => navigate(`/companies/${company.id}`)}
                          className="text-blue-600 hover:text-blue-900 p-1"
                          title={t('navigation.companies.view')}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => navigate(`/companies/${company.id}/edit`)}
                          className="text-indigo-600 hover:text-indigo-900 p-1"
                          title={t('navigation.companies.edit')}
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(company.id, company.name)}
                          disabled={deletingId === company.id}
                          className="text-red-600 hover:text-red-900 p-1 disabled:opacity-50"
                          title={t('navigation.companies.delete')}
                        >
                          {deletingId === company.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </ResponsiveTable>
          </TableScrollContainer>
        )}
      </div>

      {/* Bulk Delete Confirmation Dialog */}
      {bulkDeleteConfirm.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">{t('common.confirmDelete', { name: '' }).trim() || 'Confirmer la suppression'}</h3>
            <p className="text-gray-600 mb-6">
              Êtes-vous sûr de vouloir supprimer <strong>{bulkDeleteConfirm.companyIds.length}</strong> entreprise{bulkDeleteConfirm.companyIds.length !== 1 ? 's' : ''} ?
              {` ${t('common.cannotUndo')}`}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelBulkDelete}
                disabled={bulkDeleting}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                {t('actions.cancel')}
              </button>
              <button
                onClick={confirmBulkDelete}
                disabled={bulkDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {bulkDeleting ? t('states.loading') : t('actions.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            {t('navigation.companies.showingResults', { 
              start: (currentPage - 1) * 20 + 1, 
              end: Math.min(currentPage * 20, total), 
              total 
            })}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              {t('common.previous')}
            </button>
            <span className="px-3 py-1 text-sm">
              {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              {t('common.next')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}



